import { DietDb } from 'lib/cosmos/diet/dietDb.js';
import { FoodDb } from 'lib/cosmos/food/foodDb.js';
import { roundHelper } from 'lib/helpers/round.helper.class.js';
import { IAzureFoodSearchItem } from 'lib/search/search.models.js';
import { ISingleSeries } from 'lib/ui/graphs/index.js';

export interface IFoodNutritionGraphConfig {
	singleSeries: ISingleSeries[];
	colorScheme: string[];
}

export class FoodHelper {
	getGramsPortionCodename(): string {
		return '_grams';
	}

	getAmountText(foodAmount: DietDb.IDietFoodAmount, food: FoodDb.IFoodItem | undefined): string {
		const sourcePortions: FoodDb.IFoodPortion[] = [...(food?.portions ?? []), this.getGramsBasedFoodPortion()];
		const portion = sourcePortions.find((m) => m.id === foodAmount.portionId);

		if (!portion) {
			return `${foodAmount.amount}`;
		}

		if (portion.id === this.getGramsBasedFoodPortion().id) {
			return `${foodAmount.amount}g`;
		}

		return `${foodAmount.amount} servings of ${portion.name}`;
	}

	calculateFoodNutrientsForContainer(
		container: DietDb.IDietFoodContainer,
		foods: readonly FoodDb.IFoodItem[]
	): FoodDb.IFoodNutrients {
		const nutrients: FoodDb.IFoodNutrients = {
			kcal: 0,
			carbs: 0,
			fats: 0,
			proteins: 0,
			fiber: 0,
			sodium: 0,
			starch: 0,
			sugars: 0,
			water: 0
		};

		if (container.foods.length) {
			for (const containerFood of container.foods) {
				const linkedFood = foods.find((m) => m.id === containerFood.foodId);

				if (!linkedFood) {
					// food not available
					continue;
				}

				const foodNutrition = this.calculateFoodNutrientsForAmount(linkedFood, containerFood.foodAmount);

				nutrients.kcal += foodNutrition.kcal;
				nutrients.carbs += foodNutrition.carbs;
				nutrients.fats += foodNutrition.fats;
				nutrients.proteins += foodNutrition.proteins;
				nutrients.fiber += foodNutrition.fiber;
				nutrients.sodium += foodNutrition.sodium;
				nutrients.starch += foodNutrition.starch;
				nutrients.sugars += foodNutrition.sugars;
				nutrients.water += foodNutrition.water;
			}
		}

		return {
			kcal: this.roundNutritionValue(nutrients.kcal),
			carbs: this.roundNutritionValue(nutrients.carbs),
			fats: this.roundNutritionValue(nutrients.fats),
			proteins: this.roundNutritionValue(nutrients.proteins),
			fiber: this.roundNutritionValue(nutrients.fiber),
			sodium: this.roundNutritionValue(nutrients.sodium),
			starch: this.roundNutritionValue(nutrients.starch),
			sugars: this.roundNutritionValue(nutrients.sugars),
			water: this.roundNutritionValue(nutrients.water)
		};
	}

	calculateFoodNutrientsForDiet(diet: DietDb.IDietItem, foods: readonly FoodDb.IFoodItem[]): FoodDb.IFoodNutrients {
		const nutrients: FoodDb.IFoodNutrients = {
			kcal: 0,
			carbs: 0,
			fats: 0,
			proteins: 0,
			fiber: 0,
			sodium: 0,
			starch: 0,
			sugars: 0,
			water: 0
		};

		for (const foodContainer of diet.foodContainers) {
			const nutritionForContainer = this.calculateFoodNutrientsForContainer(foodContainer, foods);

			nutrients.kcal += nutritionForContainer.kcal;
			nutrients.carbs += nutritionForContainer.carbs;
			nutrients.fats += nutritionForContainer.fats;
			nutrients.proteins += nutritionForContainer.proteins;
			nutrients.fiber += nutritionForContainer.fiber;
			nutrients.sodium += nutritionForContainer.sodium;
			nutrients.starch += nutritionForContainer.starch;
			nutrients.sugars += nutritionForContainer.sugars;
			nutrients.water += nutritionForContainer.water;
		}

		return {
			kcal: this.roundNutritionValue(nutrients.kcal),
			carbs: this.roundNutritionValue(nutrients.carbs),
			fats: this.roundNutritionValue(nutrients.fats),
			proteins: this.roundNutritionValue(nutrients.proteins),
			fiber: this.roundNutritionValue(nutrients.fiber),
			sodium: this.roundNutritionValue(nutrients.sodium),
			starch: this.roundNutritionValue(nutrients.starch),
			sugars: this.roundNutritionValue(nutrients.sugars),
			water: this.roundNutritionValue(nutrients.water)
		};
	}

	calculateFoodNutrientsForAmount(
		food: FoodDb.IFoodItem | undefined,
		amount: DietDb.IDietFoodAmount
	): FoodDb.IFoodNutrients {
		const nutrients = food?.nutrients;
		const portionGrams = amount.gramsPerAmount * amount.amount;

		if (nutrients && portionGrams) {
			return {
				kcal: this.roundNutritionValue((nutrients.kcal * portionGrams) / 100),
				carbs: this.roundNutritionValue((nutrients.carbs * portionGrams) / 100),
				fats: this.roundNutritionValue((nutrients.fats * portionGrams) / 100),
				proteins: this.roundNutritionValue((nutrients.proteins * portionGrams) / 100),
				fiber: this.roundNutritionValue((nutrients.fiber * portionGrams) / 100),
				sodium: this.roundNutritionValue((nutrients.sodium * portionGrams) / 100),
				starch: this.roundNutritionValue((nutrients.starch * portionGrams) / 100),
				sugars: this.roundNutritionValue((nutrients.sugars * portionGrams) / 100),
				water: this.roundNutritionValue((nutrients.water * portionGrams) / 100)
			};
		}

		return {
			kcal: 0,
			carbs: 0,
			fats: 0,
			proteins: 0,
			fiber: 0,
			sodium: 0,
			starch: 0,
			sugars: 0,
			water: 0
		};
	}

	getGramsBasedFoodPortion(): FoodDb.IFoodPortion {
		return {
			id: this.getGramsPortionCodename(),
			amount: 100,
			gramWeight: 1,
			name: `Grams`,
			usdaFoodPortionId: undefined
		};
	}

	getFoodSearchItemNutritionGraphConfig(food: IAzureFoodSearchItem): IFoodNutritionGraphConfig {
		return {
			singleSeries: [
				{
					name: 'Fats (per 100g)',
					value: food?.fats ?? 0
				},
				{
					name: 'Proteins (per 100g)',
					value: food?.proteins ?? 0
				},
				{
					name: 'Carbs (per 100g)',
					value: food?.carbs ?? 0
				}
			],
			colorScheme: this.getGraphFoodSheme()
		};
	}

	getNutritionGraphConfig(
		nutrients: FoodDb.IFoodNutrients | undefined,
		config: {
			per100g: boolean;
		}
	): IFoodNutritionGraphConfig {
		const per100gramsText: string = `${config.per100g ? ' (per 100g)' : ''}`;
		return {
			singleSeries: [
				{
					name: `Fats${per100gramsText}`,
					value: nutrients?.fats ?? 0
				},
				{
					name: `Proteins${per100gramsText}`,
					value: nutrients?.proteins ?? 0
				},
				{
					name: `Carbs${per100gramsText}`,
					value: nutrients?.carbs ?? 0
				}
			],
			colorScheme: this.getGraphFoodSheme()
		};
	}

	private getGraphFoodSheme(): string[] {
		return ['#C70039', '#2196F3', '#FF9800'];
	}

	private roundNutritionValue(value: number): number {
		return roundHelper.roundTo(value, 1);
	}
}

export const foodHelper = new FoodHelper();
